/**
 * Tracks in-flight, asynchronous form value-change commits (Form.onValueChangeAsync)
 * per component uid.
 *
 * A field change (e.g. committed by blurring the focused input) is applied to the
 * data model asynchronously: the field's `onChange` returns immediately while
 * `Form.onValueChangeAsync` awaits validation and the actual model update. Save
 * flows force a blur to commit the pending input and then immediately check
 * `isDataModified` - which would race the still-running commit.
 *
 * Callers register the start/end of such a commit via {@link beginValueChange} /
 * {@link endValueChange}, and save flows await {@link waitForPendingValueChanges}
 * after forcing the blur, so the modified-check sees the committed value.
 */

type Waiter = {
    matches : ( componentUid : string ) => boolean;
    resolve : () => void;
};

const counters : Map<string, number> = new Map();
const waiters : Set<Waiter>          = new Set();

/**
 * Whether there is at least one in-flight value change for a component uid
 * accepted by the given matcher.
 *
 * @param matches predicate applied to every component uid with pending changes
 * @returns true if a matching component still has pending changes
 */
function hasPending( matches : ( componentUid : string ) => boolean ) : boolean {
    for ( const componentUid of counters.keys() ) {
        if ( matches( componentUid ) ) {
            return true;
        }
    }

    return false;
}

/**
 * Resolve every waiter whose matcher no longer has pending changes.
 */
function notifyWaiters() : void {
    for ( const waiter of Array.from( waiters ) ) {
        if ( !hasPending( waiter.matches ) ) {
            waiters.delete( waiter );
            waiter.resolve();
        }
    }
}

/**
 * Register the start of an asynchronous value-change commit for a component.
 *
 * Must be called synchronously (before the first await) so that a save flow
 * forcing a blur observes the pending change before checking it.
 *
 * @param componentUid the uid of the form component the change belongs to
 */
export function beginValueChange( componentUid : string ) : void {
    counters.set( componentUid, ( counters.get( componentUid ) ?? 0 ) + 1 );
}

/**
 * Register the completion of an asynchronous value-change commit for a component.
 *
 * @param componentUid the uid of the form component the change belongs to
 */
export function endValueChange( componentUid : string ) : void {
    const remaining = ( counters.get( componentUid ) ?? 0 ) - 1;

    if ( remaining > 0 ) {
        counters.set( componentUid, remaining );
    }
    else {
        counters.delete( componentUid );

        notifyWaiters();
    }
}

/**
 * Wait until there are no more in-flight value-change commits for any component
 * uid accepted by the given matcher.
 *
 * @param matches predicate selecting the relevant component uids
 * @param timeoutMs safety net after which the wait resolves regardless (default 3000)
 * @returns a promise resolved once no matching component has pending changes
 */
export function waitForPendingValueChanges( matches : ( componentUid : string ) => boolean, timeoutMs : number = 3000 ) : Promise<void> {
    if ( !hasPending( matches ) ) {
        return Promise.resolve();
    }

    return new Promise<void>( resolve => {
        const waiter : Waiter = {
            matches,
            resolve: () => {
                clearTimeout( timeoutHandle );
                resolve();
            }
        };

        const timeoutHandle = setTimeout( () => {
            waiters.delete( waiter );
            resolve();
        }, timeoutMs );

        waiters.add( waiter );
    } );
}
